import React, { Component, ComponentType } from 'react';
import { withRouter } from 'react-router';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { GridComponent } from '@syncfusion/ej2-react-grids';
import { DataManager } from '@syncfusion/ej2-data';
import { SortDirection } from '@syncfusion/ej2-grids';

import store, { RootState } from '../../store';
import { ITrackingsListContainerStateProps, ITrackingsListContainerDispatchProps } from '../../interfaces/props/ITrackingsListProps';
import * as trackingsListStore from '../../store/modules/trackingsList.store';
import * as listGridStore from '../../store/modules/listGrid.store';
import { URL } from '../../common/constants';
import { UserType } from '../../common/model/enumerations/userType.model';
import { RoleNormalizedName } from '../../common/model/enumerations/roleNormalizedName.model';
import { queryBuilderService, oDataService, rolesService, dataManagerService, trackingsService, userSettingsService } from '../../services';
import * as currentUserUtils from '../../utils/currentUser.utils';
import * as gridUtils from '../../utils/grid.utils';
import { notifyError, notifySuccess, notifyInfo } from '../../utils/toast.utils';
import TrackingsListFilter from './trackingsListFilter/trackingsListFilter';
import TrackingsGrid from './trackingsGrid/trackingsGrid';
import { getAllFilterRules, getInitialQuery } from '../../store/modules/trackingsList.store';
import { IUserSetting } from '../../common/model/userSetting.model';
import * as userSettingStore from '../../store/modules/userSetting.store';
import { IFile } from '../../common/model/responseError.model';

type TrackingsListContainerPropsType = PropsFromRedux & WithTranslation;

class TrackingsListContainer extends Component<TrackingsListContainerPropsType> {
  public trackingsGridComponent: GridComponent = null;
  public trackingsDataManager: DataManager = null;
  public trackingsToDownloadDataManager: DataManager = null;

  public constructor(props: TrackingsListContainerPropsType) {
    super(props);

    const { reset } = this.props;

    this.trackingsDataManager = dataManagerService.buildDataManager(URL.ODATA.TRACKING_FILTERS);
    this.trackingsToDownloadDataManager = dataManagerService.buildDataManager(URL.ODATA.TRACKING_FILTERS_EXPORT);

    reset();
  }

  public componentDidMount() {
    this.init();
  }

  private init() {
    const { isFetching, fetchAllOptions, areOptionsLoaded } = this.props;

    if (!areOptionsLoaded) {
      if (!isFetching) {
        fetchAllOptions(trackingsService);
      }
      return;
    }
  }

  public componentDidUpdate(prevProps: TrackingsListContainerPropsType) {
    if (
      prevProps.isCurrentUserInternal !== this.props.isCurrentUserInternal
      || !this.props.isCurrentUserInternal && (prevProps.currentUserCompany !== this.props.currentUserCompany)
    ) {
      setTimeout(() => this.props.resetHard());
    }

    //this.init();
  }

  public componentWillUnmount() {
    this.props.reset();
  }

  public onFilterReportNumberChange(reportNumber: string) {
    this.props.setFilterReportNumber(reportNumber);
  }

  public onFilterVersionChange(version: string) {
    this.props.setFilterVersion(version);
  }

  public onFilterSelectionNumberChange(selectionNumber: string) {
    this.props.setFilterSelectionNumber(selectionNumber);
  }

  public onFilterReferenceChange(reference: string) {
    this.props.setFilterReference(reference);
  }

  public onFilterModelChange(model: string) {
    this.props.setFilterModel(model);
  }

  public onFilterShipmentDateChange(shipmentDate: Date[]) {
    this.props.setFilterShipmentDate(shipmentDate);
  }

  public onFilterSeasonCodesChange(seasonCode: string[]) {
    this.props.setFilterSeasonCodes(seasonCode);
  }

  public onFilterOfficeIdsChange(officeCode: string[]) {
    this.props.setFilterOfficeCodes(officeCode);
  }

  public onFilterButtonClick() {
    if (!this.checkIsSimpleFilterValid()) {
      return;
    }

    const filters = getAllFilterRules(store.getState());
    if (filters.myRules.length === 0) {
      notifyError(this.props.i18n.t('trackingsList.errors.mustIndicateAnyFilter'));
      return;
    }

    this.props.setCurrentPage(trackingsListStore.Type.SET_CURRENT_PAGE, 1);
    this.fetchTrackings();
  }

  public onCurrentPageChange(currentPage: number) {
    this.props.setCurrentPage(trackingsListStore.Type.SET_CURRENT_PAGE, currentPage);
    this.fetchTrackings();
  }

  public onPageSizeChange(pageSize: number) {
    this.props.setPageSize(trackingsListStore.Type.SET_PAGE_SIZE, pageSize);
    this.fetchTrackings();
  }

  public onSortingChange(field: string, direction: SortDirection) {
    this.props.setSorting(trackingsListStore.Type.SET_SORTING, field, direction);
    this.fetchTrackings();
  }

  public isEciUser(): boolean {
    return currentUserUtils.isEciUser(this.props.currentUserCompany, this.props.currentUserChainName);
  }

  public isEciImplant(): boolean {
    return this.props.isEciImplant;
  }

  public render() {
    const {
      trackings,
      filter,
      seasonOptions,
      officeOptions,
      isFetching,
      searchDate,
      currentPage,
      pageSize,
      totalCount,
      sortField,
      sortDirection,
      isCurrentUserInternal,
      currentUserChainName,
      i18n,
      currentUserEmail,
      currentUserId
    } = this.props;

    return (
      <div>
        <div className='row'>
          <div className='col'>
            <h2>{i18n.t('trackingsList.title')}</h2>
          </div>
        </div>
        <div className='row'>
          <div className='col'>
            <div className='mb-3'>
              <TrackingsListFilter
                filter={filter}
                seasonOptions={seasonOptions}
                officeOptions={officeOptions}
                isFetching={isFetching}
                isCurrentUserInternal={isCurrentUserInternal}
                hasCurrentUserChain={!!currentUserChainName}
                isEciUser={this.isEciUser()}
                isEciImplant={this.isEciImplant()}
                onReportNumberChange={(reportNumber) => this.onFilterReportNumberChange(reportNumber)}
                onVersionChange={(version) => this.onFilterVersionChange(version)}
                onSelectionNumberChange={(selectionNumber) => this.onFilterSelectionNumberChange(selectionNumber)}
                onReferenceChange={(reference) => this.onFilterReferenceChange(reference)}
                onModelChange={(model) => this.onFilterModelChange(model)}
                onShipmentDateChange={(shipmentDate) => this.onFilterShipmentDateChange(shipmentDate)}
                onOfficeCodesChange={(officeCodes) => this.onFilterOfficeIdsChange(officeCodes)}
                onSeasonCodesChange={(seasonCodes) => this.onFilterSeasonCodesChange(seasonCodes)}
                onFilterButtonClick={() => this.onFilterButtonClick()}
              />
            </div>
            <TrackingsGrid
               trackings={trackings}
               isFetching={isFetching}
               searchDate={searchDate}
               currentPage={currentPage}
               pageSize={pageSize}
               totalCount={totalCount}
               sortField={sortField}
               sortDirection={sortDirection}
               isCurrentUserInternal={isCurrentUserInternal}
               currentUserEmail={currentUserEmail}
               currentUserId={currentUserId}
               hasCurrentUserChain={!!currentUserChainName}
               isEciUser={this.isEciUser()}
               gridRef={(grid) => this.trackingsGridComponent = grid}
               onCurrentPageChange={(cp) => this.onCurrentPageChange(cp)}
               onPageSizeChange={(ps) => this.onPageSizeChange(ps)}
               onSaveColumnsButtonClick={(userSetting: IUserSetting) => this.saveColumnsSettings(userSetting)}
               onCleanColumnsButtonClick={(key: string) => this.cleanColumnsSettings(key)}
               onExportToExcelButtonClick={() => this.exportToExcel()}
             />
          </div>
        </div>
      </div>
    );
  }

  private fetchTrackings() {
    const { isFetching, fetchTrackings } = this.props;

    if (!isFetching) {
      fetchTrackings(queryBuilderService, oDataService, this.trackingsDataManager);
    }
  }

  private GetBlobFile(file: IFile): Blob {
    if (file !== undefined && file != null && file.stream !== undefined && file.stream != null) {
      // Convertir el archivo base64 a un Blob
      const byteCharacters = atob(file.stream);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: file.mimeType });

      return blob;
    }

    return null;
  }

  private async exportToExcel() {
    notifyInfo(this.props.i18n.t('trackingsList.messages.exportToExcel'));

    if (this.trackingsGridComponent) {
      this.trackingsGridComponent.showSpinner();
    }

    try {
      const filters = getAllFilterRules(store.getState());
      const query = getInitialQuery();

      const response = await trackingsService.exportToExcel(this.trackingsToDownloadDataManager, query, filters);

      if (response.actual !== undefined && response.actual != null && Array.isArray(response.actual) && response.actual.length == 1) {
        const fileResponse = response.actual[0] as IFile;
        const file = this.GetBlobFile(fileResponse);

        if (file != null) {
          const url = window.URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = url;
          link.download = fileResponse.fileName;
          link.click();
          window.URL.revokeObjectURL(url);
        } else {
          notifyError(this.props.i18n.t('trackingsList.errors.cannotExportTrackingsToExcel'));
        }
      } else {
        notifyError(this.props.i18n.t('trackingsList.errors.cannotExportTrackingsToExcel'));
      }
    } catch (error) {
      notifyError(this.props.i18n.t('trackingsList.errors.cannotExportTrackingsToExcel'));
    } finally {
      if (this.trackingsGridComponent) {
        this.trackingsGridComponent.hideSpinner();
      }
    }
  }

  private async saveColumnsSettings(userSetting: IUserSetting) {
    try {
      const info = await userSettingsService.refreshUserSetting(userSetting);

      if (info) {
        this.props.saveUserSetting(info);
        notifySuccess('La configuración personalizada se ha guardado correctamente.');
      } else {
        notifyError('');
      }
    } catch (error: any) {
      let newErrorMessage = 'errorMessage';
      if (error && error['UnknownError']) {
        const valueError = error['UnknownError'];
        if (Array.isArray(valueError)) {
          newErrorMessage = valueError.join(' ');
        } else {
          newErrorMessage = valueError;
        }
      }

      notifyError(newErrorMessage);
    }
  }

  private async cleanColumnsSettings(key: string) {
    const { currentUserId } = this.props;

    try {
      gridUtils.cleanColumnsGridInLocalStorage(currentUserId, this.trackingsGridComponent);
      const info = await userSettingsService.getUserSetting(currentUserId, key);

      if (info !== undefined && info != null && (info as any) != '') {
        this.props.saveUserSetting(info);
        const result = JSON.parse(info.jsonValue) as any[];
        gridUtils.saveColumnsGridInLocalStorage(currentUserId, this.trackingsGridComponent, result);
      }
    } catch (error: any) {
      let newErrorMessage = 'errorMessage';
      if (error && error['UnknownError']) {
        const valueError = error['UnknownError'];
        if (Array.isArray(valueError)) {
          newErrorMessage = valueError.join(' ');
        } else {
          newErrorMessage = valueError;
        }
      }

      notifyError(newErrorMessage);
    }
  }

  private checkIsSimpleFilterValid(): boolean {
    return true;
  }
}

const mapStateToProps = (state: RootState): ITrackingsListContainerStateProps => ({
  trackings: state.trackingsListStore.trackings,
  filter: state.trackingsListStore.filter,
  seasonOptions: state.trackingsListStore.seasonOptions,
  officeOptions: state.trackingsListStore.officeOptions,
  isFetching: state.trackingsListStore.grid.isFetching,
  searchDate: state.trackingsListStore.grid.searchDate,
  currentPage: state.trackingsListStore.grid.currentPage,
  pageSize: state.trackingsListStore.grid.pageSize,
  totalCount: state.trackingsListStore.grid.totalCount,
  sortField: state.trackingsListStore.grid.sortField,
  sortDirection: state.trackingsListStore.grid.sortDirection,
  isCurrentUserInternal: state.currentUserStore.user.type === UserType.INTERNAL,
  isCurrentUserAdmin: rolesService.hasAnyRole(state.currentUserStore.jwtInfo, [RoleNormalizedName.ADMIN]),
  currentUserCompany: state.currentUserStore.company,
  currentUserChainName: currentUserUtils.getCurrentUserChainName(state.currentUserStore.user, state.currentUserStore.jwtInfo),
  currentUserEmail: state.currentUserStore.user.email,
  currentUserId: state.currentUserStore.user.id,
  isEciImplant: currentUserUtils.isEciImplant(state.currentUserStore.user),
  areOptionsLoaded: state.trackingsListStore.areOptionsLoaded
});

const mapDispatchToProps: ITrackingsListContainerDispatchProps = {
  reset: trackingsListStore.reset,
  resetHard: trackingsListStore.resetHard,
  setFilterReportNumber: trackingsListStore.setFilterReportNumber,
  setFilterVersion: trackingsListStore.setFilterVersion,
  setFilterSelectionNumber: trackingsListStore.setFilterSelectionNumber,
  setFilterReference: trackingsListStore.setFilterReference,
  setFilterModel: trackingsListStore.setFilterModel,
  setCurrentPage: listGridStore.setCurrentPage,
  setPageSize: listGridStore.setPageSize,
  setSorting: listGridStore.setSorting,
  fetchTrackings: trackingsListStore.fetchTrackings,

  setFilterOfficeCodes: trackingsListStore.setFilterOfficeCodes,
  setFilterSeasonCodes: trackingsListStore.setFilterSeasonCodes,
  setFilterShipmentDate: trackingsListStore.setFilterShipmentDate,
  fetchAllOptions: trackingsListStore.fetchOptionsFieldSettings,
  saveUserSetting: userSettingStore.saveUserSetting
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default compose(
  withRouter,
  withTranslation(),
  connector
)(TrackingsListContainer) as ComponentType;
